import React, { useState } from 'react';
import Section from './Section';
import Icon from './Icons';
import { SKILLS, CORE_STACK, ROLE_INDEX } from '../data/skills';
import { roleVar } from './graphPalette';
import { useHighlight, skillMatchesTag } from '../context/Highlight';

const LEVEL_LABEL = { 3: 'Core', 2: 'Working', 1: 'Familiar' };

function jumpTo(ref) {
  const id = ref.type === 'experience' ? `exp-${ref.id}` : `proj-${ref.id}`;
  const el = document.getElementById(id);
  if (!el) return;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });
  el.classList.remove('flash'); void el.offsetWidth; el.classList.add('flash');
}

function Dots({ refs }) {
  return (
    <span className="sk__dots" aria-hidden="true">
      {refs.map((r, i) => (
        <i key={i} style={r.type === 'experience' ? { background: roleVar(ROLE_INDEX[r.id] ?? 0) } : undefined} className={r.type === 'project' ? 'sk__dot--proj' : ''} />
      ))}
    </span>
  );
}

function Level({ level }) {
  return (
    <span className="sk__level" title={LEVEL_LABEL[level]} aria-label={LEVEL_LABEL[level]}>
      {[1, 2, 3].map((n) => <i key={n} className={n <= level ? 'on' : ''} />)}
    </span>
  );
}

function RefList({ refs }) {
  if (!refs.length) return <p className="github-empty">Personal projects &amp; practice</p>;
  return refs.map((r, i) => (
    <button key={i} className="sk__ref" onClick={() => jumpTo(r)}>
      <i style={r.type === 'experience' ? { background: roleVar(ROLE_INDEX[r.id] ?? 0) } : undefined} className={r.type === 'project' ? 'sk__dot--proj' : ''} />
      {r.label}<span>{r.type}</span>
    </button>
  ));
}

export default function Skills() {
  const [open, setOpen] = useState(null); // "cat:skill" key
  const { skill: extSkill, role: extRole, setSkill, setRole } = useHighlight();
  const isLit = (s) => (extSkill && skillMatchesTag(extSkill, s.name)) || (extRole && s.refs.some((r) => r.type === 'experience' && r.id === extRole));

  return (
    <Section id="skills" num="02" title="Skill Stack" subtitle="What I reach for, how deep it goes, and where I've used it">
      <div className="sk__legend">
        <span><Level level={3} /> Core — shipped to production</span>
        <span><Level level={2} /> Working — used in real projects</span>
        <span><Level level={1} /> Familiar</span>
        <span className="sk__legend-roles">
          <i style={{ background: roleVar(0) }} /> Earnie V2 <i style={{ background: roleVar(1) }} /> Win It All <i style={{ background: roleVar(2) }} /> iD Tech <i style={{ background: roleVar(3) }} /> Whitman <i className="sk__dot--proj" /> project
        </span>
      </div>

      <div className="sk__core">
        {CORE_STACK.map((s) => (
          <div className={`sk__card${isLit(s) ? ' sk__card--lit' : ''}`} key={s.name} onMouseEnter={() => setSkill(s.name)} onMouseLeave={() => setSkill(null)}>
            <div className="sk__card-top">
              <span className="sk__card-icon"><Icon name={s.icon} /></span>
              <Level level={s.level} />
            </div>
            <div className="sk__card-name">{s.name}</div>
            <div className="sk__card-blurb">{s.blurb}</div>
            <div className="sk__card-refs">
              {s.refs.map((r, i) => <button key={i} onClick={() => jumpTo(r)} onMouseEnter={(e) => { e.stopPropagation(); setSkill(null); setRole(r.id); }} onMouseLeave={() => setRole(null)} style={{ '--c': roleVar(ROLE_INDEX[r.id] ?? 0) }}><i />{r.label}</button>)}
            </div>
          </div>
        ))}
      </div>

      <div className="sk__grid">
        {SKILLS.map((cat) => (
          <div className="sk__group reveal" key={cat.category}>
            <div className="sk__group-head"><Icon name={cat.icon} /><h4>{cat.category}</h4></div>
            <div className="sk__chips">
              {cat.skills.map((s) => {
                const key = `${cat.category}:${s.name}`;
                const isOpen = open === key;
                return (
                  <div key={s.name} className={`sk__chip-wrap${isOpen ? ' open' : ''}`}>
                    <button className={`sk__chip sk__chip--l${s.level}${isOpen ? ' sk__chip--open' : ''}${isLit(s) ? ' sk__chip--lit' : ''}`} onClick={() => setOpen(isOpen ? null : key)} aria-expanded={isOpen}
                      onMouseEnter={() => setSkill(s.name)} onMouseLeave={() => setSkill(null)}>
                      <span>{s.name}</span>
                      <Level level={s.level} />
                      <Dots refs={s.refs} />
                    </button>
                    {isOpen && (
                      <div className="sk__pop">
                        <div className="sk__pop-title">{s.name} · {LEVEL_LABEL[s.level]}</div>
                        <RefList refs={s.refs} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}
